import { useAxios } from "./useAxios"
import { Pagination } from "@/interfaces/Pagination"
import { MessageStatusEnum } from "@/enums/messages/MessageStatusEnum"
import { useBot } from "./useBot"
import { Message } from "@/interfaces/messages/Message"
import { MessageRecipientTypeEnum } from "@/enums/messages/MessageRecipientTypeEnum"
import { AcceptableInputValue } from "radix-vue/dist/TagsInput/TagsInputRoot"

const axios = useAxios()

export interface CreateMessageDTO {
  recipientType: MessageRecipientTypeEnum
  message: string
  description: string
  abandonedChat?: boolean
  status: MessageStatusEnum
  sendAt: Date
  sendingInterval?: number | null
  sendingRange?: number
  topMedia: File
  startMedia: File
  paymentPlanIds?: AcceptableInputValue[]
  buttonIds?: AcceptableInputValue[]
  groupIds?: AcceptableInputValue[]
  downsellPaymentPlanIds?: AcceptableInputValue[]
  targetPaymentPlanIds?: AcceptableInputValue[]
  flowId?: number | string
  nodeId?: string
  hasSpoiler?: boolean
}

export interface UpdateMessageDTO extends Partial<CreateMessageDTO> {
  status: MessageStatusEnum
}

export interface GetAllMessagesParams {
  page?: string
  perPage?: string
}

export interface CountMessageRecipientsParams {
  recipientType: MessageRecipientTypeEnum
  targetPaymentPlanIds?: AcceptableInputValue[]
  flowId?: number | string
  nodeId?: string
}

export function useMessage() {
  const { selectedBot } = useBot()

  const create = async (payload: CreateMessageDTO, paymentPlanIds: AcceptableInputValue[] = [], buttonIds: AcceptableInputValue[] = [], groupIds: AcceptableInputValue[] = [], downsellPaymentPlanIds: AcceptableInputValue[] = [], targetPaymentPlanIds: AcceptableInputValue[] = []): Promise<Message> => {
    const formData = new FormData()

    for (const key in payload) {
      if (payload[key as keyof typeof payload] !== null && payload[key as keyof typeof payload] !== '') {
        const value = payload[key as keyof typeof payload]

        // Se o valor for um array de arquivos, fazer append de cada arquivo individualmente
        if (Array.isArray(value)) {
          value.forEach((file, index) => {
            if (file instanceof File) {
              formData.append(`${key}`, file)
            } else {
              formData.append(`${key}[${index}]`, value[index] as string)
            }
          })
        } else {
          // Se não for um array, simplesmente fazer o append normal
          formData.append(key, value as string | File)
        }
      }
    }

    paymentPlanIds.forEach((id, index) => formData.append(`paymentPlanIds[${index}]`, String(id)))
    buttonIds.forEach((id, index) => formData.append(`buttonIds[${index}]`, String(id)))
    groupIds.forEach((id, index) => formData.append(`groupIds[${index}]`, String(id)))
    downsellPaymentPlanIds.forEach((id, index) => formData.append(`downsellPaymentPlanIds[${index}]`, String(id)))
    targetPaymentPlanIds.forEach((id, index) => formData.append(`targetPaymentPlanIds[${index}]`, String(id)))

    const response = await axios.post(`/bots/${selectedBot.value?.id}/messages`, formData)

    return response.data
  }

  const update = async (id: number, payload: UpdateMessageDTO, paymentPlanIds: AcceptableInputValue[] = [], buttonIds: AcceptableInputValue[] = [], groupIds: AcceptableInputValue[] = [], downsellPaymentPlanIds: AcceptableInputValue[] = [], targetPaymentPlanIds: AcceptableInputValue[] = []): Promise<Message> => {
    const formData = new FormData()

    for (const key in payload) {
      const value = payload[key as keyof typeof payload]

      // Se o valor for um array de arquivos, fazer append de cada arquivo individualmente
      if (Array.isArray(value)) {
        value.forEach((file, index) => {
          if (file instanceof File) {
            formData.append(`${key}`, file)
          } else {
            formData.append(`${key}[${index}]`, value[index] as string)
          }
        })
      } else if (value !== undefined) {
        // Se não for um array, simplesmente fazer o append normal
        formData.append(key, value as string | File)
      }
    }

    paymentPlanIds.forEach((id, index) => formData.append(`paymentPlanIds[${index}]`, String(id)))
    buttonIds.forEach((id, index) => formData.append(`buttonIds[${index}]`, String(id)))
    groupIds.forEach((id, index) => formData.append(`groupIds[${index}]`, String(id)))
    downsellPaymentPlanIds.forEach((id, index) => formData.append(`downsellPaymentPlanIds[${index}]`, String(id)))
    targetPaymentPlanIds.forEach((id, index) => formData.append(`targetPaymentPlanIds[${index}]`, String(id)))

    const response = await axios.patch(`/bots/${selectedBot.value?.id}/messages/${id}`, formData)

    return response.data
  }

  const getAll = async (params?: GetAllMessagesParams): Promise<Pagination<Message>> => {
    if (params) {
      params = Object.fromEntries(
        Object.entries(params).filter(([param, value]) => !!value)
      )
    }

    const response = await axios.get(`/bots/${selectedBot.value?.id}/messages`, { params })

    return response.data
  }

  const getById = async (id: number): Promise<Message> => {
    const response = await axios.get(`/bots/${selectedBot.value?.id}/messages/${id}`)

    return response.data
  }

  const destroy = async (id: number): Promise<void> => {
    await axios.delete(`/bots/${selectedBot.value?.id}/messages/${id}`)
  }

  const countRecipients = async (params: CountMessageRecipientsParams): Promise<number | null> => {
    const response = await axios.get(`/bots/${selectedBot.value?.id}/messages/recipients/count`, {
      params: {
        recipientType: params.recipientType,
        targetPaymentPlanIds: params.targetPaymentPlanIds,
        flowId: params.flowId,
        nodeId: params.nodeId
      }
    })

    return response.data.count
  }

  return {
    create,
    getAll,
    getById,
    update,
    destroy,
    countRecipients
  }
}